/**
 * The shared renderer for a `402 plan_limit` refusal.
 *
 * Every hard block is the same shape — the pill, a sentence naming the number
 * and the plan, the way out, and `See plans` — composed entirely from the 402
 * body, so a limit changed in the plan editor changes every message with no
 * copy edit. There is deliberately no animation: a refusal should read as a
 * fact, not an event.
 *
 * Two variants:
 *  - `inline`  — the tinted #fff5f5 block that sits where the refused action
 *                lives (users page, builder). Never a modal mid-edit.
 *  - `modal`   — content for a modal card (the forms list); the caller owns
 *                the overlay and card.
 * `children` is the slot for a second action ("Manage my 5 forms").
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '../admin-limits.css';
import { PlanLimitBody, limitNoun, planDisplayName } from '../api/limits';

export const PLANS_PATH = '/admin/settings/billing/plans';

/** "You've used all 5 forms on Free" — the sentence, from the body alone. */
export function limitSentence(body: PlanLimitBody): string {
  // A ceiling of zero is a feature the plan doesn't include (the server
  // refuses features as allowed: 0) — "you've used all 0" is not a sentence.
  if (body.allowed === 0) {
    const noun = limitNoun(body.limit, 2);
    return `${noun[0].toUpperCase()}${noun.slice(1)} isn't included on ${planDisplayName(body.plan)}`;
  }
  const all = body.allowed === 1 ? 'the only' : `all ${body.allowed}`;
  return `You've used ${all} ${limitNoun(body.limit, body.allowed)} on ${planDisplayName(body.plan)}`;
}

/**
 * The way out, per limit. Only what the 402 body knows: the upgrade target is
 * named, but its numbers and price are not invented here — `See plans` holds
 * the numbers.
 */
export function limitWayOut(body: PlanLimitBody): string {
  const up = planDisplayName(body.upgradeTo);
  switch (body.limit) {
    case 'forms':
      return up
        ? `${up} gives you more forms. You can also delete or archive a form you're finished with and the slot comes back immediately.`
        : `Delete or archive a form you're finished with and the slot comes back immediately.`;
    case 'users':
      return up
        ? `Remove someone to free a seat, or move to ${up} for more seats.`
        : `Remove someone to free a seat.`;
    case 'fieldsPerForm':
      return up
        ? `Delete a field or a widget to add another, or move to ${up} for a higher ceiling.`
        : `Delete a field or a widget to add another.`;
    case 'aiGenerationsPerMonth':
      return up
        ? `The counter resets on the 1st, or ${up} includes more each month. You can still build any form by hand or from a template.`
        : `The counter resets on the 1st. You can still build any form by hand or from a template.`;
    case 'aiAssistantMessagesPerMonth':
      return up
        ? `The counter resets on the 1st, or ${up} includes more each month. Everything the assistant does can still be done by hand.`
        : `The counter resets on the 1st. Everything the assistant does can still be done by hand.`;
    default:
      if (body.allowed === 0) {
        return up ? `${up} includes it.` : `You're already on the highest plan for this.`;
      }
      return up
        ? `${up} raises this ceiling.`
        : `You're already on the highest plan for this.`;
  }
}

export default function PlanLimitBlock({ body, variant = 'inline', children }: {
  body: PlanLimitBody;
  variant?: 'inline' | 'modal';
  /** slot for a second action beside `See plans` */
  children?: ReactNode;
}) {
  const sentence = limitSentence(body);
  const wayOut = limitWayOut(body);

  if (variant === 'modal') {
    return (
      <div>
        <span className="lim-pill">Plan limit</span>
        <h3 className="lim-heading">{sentence}</h3>
        <p className="lim-body">{wayOut}</p>
        <div className="lim-actions">
          <Link className="ad-btn primary" to={PLANS_PATH}>See plans</Link>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="lim-block">
      <div><span className="lim-pill">Plan limit</span></div>
      <div>
        <b>{sentence}.</b> {wayOut}{' '}
        <Link className="lim-link" to={PLANS_PATH}>See plans</Link>
      </div>
      {children ? <div className="lim-actions">{children}</div> : null}
    </div>
  );
}
